/* eslint-disable import/no-extraneous-dependencies */
import { Marker } from 'react-leaflet';
import React, { useMemo } from 'react';
import L from 'leaflet';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { useStore } from '../../../../store';
import { useMapContext } from '../context/map-context';
import { createAntennaIcon } from './map-icons/icons';
import { IAntenna } from './MapComponent.types';

interface ICustomAntennasProps {
  antennas: IAntenna[];
}

export const CustomAntennas = (props: ICustomAntennasProps):JSX.Element => {
  const { antennas: realAntennas } = props;
  const existingAntennas = realAntennas?.length === 0 ? [] : realAntennas;
  const { mapOrigin } = useStore();
  const {
    imageAngle, rangePerDegree, mapRef,
  } = useMapContext();

  const rangeToCoordinates = (rangeX: number, rangeY: number): [number, number] | null => {
    if (!mapOrigin.data.latitude || !mapOrigin.data.longitude || !rangePerDegree) return null;

    const latitude = Number(mapOrigin.data.latitude) + rangeY / rangePerDegree.lat;
    const longitude = Number(mapOrigin.data.longitude) + rangeX / rangePerDegree.lon;

    return [latitude, longitude];
  };

  function rotatePointInPixelSpace(
    point: [number, number],
    center: [number, number],
    angle: number,
  ): [number, number] | null {
    if (!rangePerDegree || !mapRef.current) return null;

    const map = mapRef.current;
    const angleRad = (angle * Math.PI) / 180; // Positive angle to match image rotation

    // Project to pixel space
    const pointProjected = map.project(L.latLng(point));
    const centerProjected = map.project(L.latLng(center));

    // Calculate deltas in pixel space
    const deltaX = pointProjected.x - centerProjected.x;
    const deltaY = pointProjected.y - centerProjected.y;

    // Apply rotation in pixel space
    const rotatedX = deltaX * Math.cos(angleRad) - deltaY * Math.sin(angleRad);
    const rotatedY = deltaX * Math.sin(angleRad) + deltaY * Math.cos(angleRad);

    // Create rotated point in pixel space
    const rotatedPointProjected = L.point(
      centerProjected.x + rotatedX,
      centerProjected.y + rotatedY,
    );

    // Unproject back to lat/lng
    const coords = map.unproject(rotatedPointProjected);

    return [coords.lat, coords.lng];
  }

  const memoizedAntennas = useMemo(() => {
    if (!mapRef.current) return null;

    const antennas = existingAntennas?.map((antenna) => {
      // Convert range to lat/lon coordinates
      const position = rangeToCoordinates(Number(antenna?.x), Number(antenna?.y));

      if (!position) return null;

      // Rotate the position in pixel space (matching the image rotation)
      const rotatedPosition = imageAngle !== 0
        ? rotatePointInPixelSpace(
          position,
          [Number(mapOrigin.data.latitude), Number(mapOrigin.data.longitude)],
          imageAngle,
        )
        : position;

      if (!rotatedPosition) return null;

      return (
        <Marker
          key={`${antenna.id}-${antenna.x}-${antenna.y}`}
          position={[rotatedPosition[0], rotatedPosition[1]]}
          icon={createAntennaIcon(antenna)}
        />
      );
    });

    return <>{antennas}</>;
  }, [
    imageAngle,
    existingAntennas,
    mapRef.current,
    rangePerDegree,
  ]);

  return (
    <>
      {memoizedAntennas}
    </>
  );
};
